import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SetupStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  action?: () => Promise<void>;
}

export const SetupWizard: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<SetupStep[]>([
    {
      id: 'welcome',
      title: 'Welcome to biig.biz',
      description: 'Let\'s get your AI project up and running in minutes!',
      completed: false
    },
    {
      id: 'api-check',
      title: 'Check API Connection',
      description: 'Verify that your CloudFlare Workers API is running',
      completed: false,
      action: checkApiConnection
    },
    {
      id: 'database',
      title: 'Setup Database',
      description: 'Initialize your D1 database with sample data',
      completed: false,
      action: setupDatabase
    },
    {
      id: 'project-info',
      title: 'Project Configuration',
      description: 'Configure your AI project settings',
      completed: false
    },
    {
      id: 'complete',
      title: 'Ready to Build!',
      description: 'Your biig.biz platform is ready for development',
      completed: false
    }
  ]);

  const [projectConfig, setProjectConfig] = useState({
    name: '',
    description: '',
    aiModel: 'gpt-4',
    features: [] as string[]
  });

  async function checkApiConnection(): Promise<void> {
    const response = await fetch('/api/health');
    if (!response.ok) {
      throw new Error('API connection failed');
    }
  }

  async function setupDatabase(): Promise<void> {
    // In a real setup, this would run migrations
    const response = await fetch('/api/users');
    if (!response.ok) {
      throw new Error('Database connection failed');
    }
  }

  const executeStep = async (stepIndex: number) => {
    const step = steps[stepIndex];
    if (step.action) {
      try {
        await step.action();
        markStepCompleted(stepIndex);
      } catch (error) {
        console.error(`Step ${step.id} failed:`, error);
        // In a real app, show error message
      }
    } else {
      markStepCompleted(stepIndex);
    }
  };

  const markStepCompleted = (stepIndex: number) => {
    setSteps(prev => prev.map((step, index) => 
      index === stepIndex ? { ...step, completed: true } : step
    ));
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = steps[currentStep];

  const renderStepContent = () => {
    switch (currentStepData.id) {
      case 'welcome':
        return (
          <div className="text-center space-y-6">
            <div className="text-6xl">🤖</div>
            <h2 className="text-3xl font-bold text-gray-900">Welcome to biig.biz!</h2>
            <p className="text-lg text-gray-600 max-w-md mx-auto">
              This wizard will help you configure your CloudFlare full-stack starter for AI development.
            </p>
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">What you'll get:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✅ CloudFlare Workers API</li>
                <li>✅ D1 Database with AI-optimized schema</li>
                <li>✅ React frontend with Tailwind CSS</li>
                <li>✅ Ready-to-use components</li>
              </ul>
            </div>
          </div>
        );

      case 'api-check':
        return (
          <div className="text-center space-y-6">
            <div className="text-4xl">🔌</div>
            <h2 className="text-2xl font-bold text-gray-900">API Connection</h2>
            <p className="text-gray-600">
              Let's verify your CloudFlare Workers API is running properly.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
              curl http://localhost:8787/api/health
            </div>
            <button
              onClick={() => executeStep(currentStep)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Test API Connection
            </button>
          </div>
        );

      case 'database':
        return (
          <div className="text-center space-y-6">
            <div className="text-4xl">🗃️</div>
            <h2 className="text-2xl font-bold text-gray-900">Database Setup</h2>
            <p className="text-gray-600">
              Initialize your D1 database with the AI-optimized schema.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 text-left">
              <h4 className="font-semibold mb-2">Tables to create:</h4>
              <ul className="text-sm space-y-1">
                <li>• users - User management and authentication</li>
                <li>• projects - AI project configurations</li>
                <li>• files - Asset management for uploads</li>
              </ul>
            </div>
            <button
              onClick={() => executeStep(currentStep)}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
            >
              Setup Database
            </button>
          </div>
        );

      case 'project-info':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-4xl mb-4">⚙️</div>
              <h2 className="text-2xl font-bold text-gray-900">Project Configuration</h2>
              <p className="text-gray-600">Tell us about your AI project</p>
            </div>
            
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name
                </label>
                <input
                  type="text"
                  value={projectConfig.name}
                  onChange={(e) => setProjectConfig({...projectConfig, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="My AI Application"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={projectConfig.description}
                  onChange={(e) => setProjectConfig({...projectConfig, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Brief description of your AI project..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AI Model
                </label>
                <select
                  value={projectConfig.aiModel}
                  onChange={(e) => setProjectConfig({...projectConfig, aiModel: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  <option value="claude-3">Claude 3</option>
                  <option value="gemini-pro">Gemini Pro</option>
                </select>
              </div>
            </form>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center space-y-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="text-6xl"
            >
              🎉
            </motion.div>
            <h2 className="text-3xl font-bold text-gray-900">Ready to Build!</h2>
            <p className="text-lg text-gray-600 max-w-md mx-auto">
              Your biig.biz platform is configured and ready for development.
            </p>
            
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="font-semibold text-green-900 mb-4">Next Steps:</h3>
              <div className="text-left space-y-2 text-sm text-green-800">
                <div>1. Explore the <strong>Projects</strong> tab to create your first AI project</div>
                <div>2. Check out the <strong>API</strong> tab to test your endpoints</div>
                <div>3. Customize components in <code>apps/web/src/components/</code></div>
                <div>4. Add your AI model integrations</div>
              </div>
            </div>
            
            <button
              onClick={onComplete}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors text-lg font-semibold"
            >
              Start Building! 🚀
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full">
        {/* Progress Bar */}
        <div className="px-8 pt-8">
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                  index <= currentStep
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step.completed ? '✓' : index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-1 mx-2 ${
                    index < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="px-8 pb-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-8 py-6 bg-gray-50 rounded-b-xl">
          {currentStep > 0 ? (
            <button
              onClick={prevStep}
              className="text-gray-600 hover:text-gray-800 font-medium"
            >
              ← Previous
            </button>
          ) : <div />}
          
          {currentStep < steps.length - 1 ? (
            <button
              onClick={nextStep}
              disabled={currentStepData.id === 'project-info' && !projectConfig.name}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};